/**
 * MarkerActionOverlay surfaces the actions for a selected marker: edit, draw a
 * line from it, delete. It dismisses itself when the pointer leaves.
 *
 * The three actions mirror the Android marker context menu, in the same order,
 * so a game master moving between the web map and a tablet finds the same
 * choices in the same place.
 *
 * Positioning and button chrome live in {@link MapActionOverlay}, shared with
 * the tactical-line overlay — the two differ only in what they anchor to and
 * which actions they offer.
 */

import React from "react";
import { faTrash, faPen, faArrowTrendUp } from "@fortawesome/free-solid-svg-icons";
import { MapEntity } from "./mapTypes.d";
import MapActionOverlay from "./MapActionOverlay";

export interface MarkerActionOverlayProps {
    marker: MapEntity | null;
    onDelete: (markerId: string) => void;
    onEdit: (markerId: string) => void;
    /**
     * Start drawing a tactical line attached to this marker (e.g. an attack
     * line from a spotted enemy). The parent switches the map into Lines mode.
     */
    onDrawLine: (markerId: string) => void;
    onDismiss: () => void;
}

/**
 * Transparent action overlay rendered above the selected marker.
 * Renders nothing when no marker is selected.
 */
export default function MarkerActionOverlay({
    marker,
    onDelete,
    onEdit,
    onDrawLine,
    onDismiss,
}: MarkerActionOverlayProps) {
    return (
        <MapActionOverlay
            testId="marker-action-overlay"
            anchor={marker ? { lat: marker.latLng.lat, lng: marker.latLng.lng } : null}
            onDismiss={onDismiss}
            actions={
                marker
                    ? [
                          {
                              icon: faPen,
                              label: "Edit marker",
                              testId: "marker-action-edit",
                              onClick: () => onEdit(marker.id),
                          },
                          {
                              icon: faArrowTrendUp,
                              label: "Draw line from marker",
                              testId: "marker-action-draw-line",
                              onClick: () => onDrawLine(marker.id),
                          },
                          {
                              icon: faTrash,
                              label: "Delete marker",
                              testId: "marker-action-delete",
                              danger: true,
                              onClick: () => onDelete(marker.id),
                          },
                      ]
                    : []
            }
        />
    );
}
